import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import type { Sport } from '__generated__/globalTypes';
import { ReferralRewardState, ReferralState } from '__generated__/globalTypes';
import { Horizontal, SBHorizontal, Vertical } from 'atoms/layout/flex';
import { BodyL, BodyM } from 'atoms/typography';
import { Badge } from 'atoms/ui/Badge';
import { ActiveUserAvatar } from 'components/user/ActiveUserAvatar';
import { MyClubLink } from 'components/user/MyClubLink';
import { UserName } from 'components/user/UserName';
import { TimeLeft } from 'contexts/ticker/TimeLeft';
import { useReferralRewards } from 'hooks/referral/useReferralRewards';
import { useToggle } from 'hooks/useToggle';
import { isType } from 'lib/gql';

import { InProgressReferralBar } from './InProgressReferralBar';
import { ReadyToClaimReferralBar } from './ReadyToClaimReferralBar';
import type { ReferralBar_currentUser } from './__generated__/index.graphql';

type OuterProps = {
  sport: Sport;
};

type Props = OuterProps & {
  currentUser: NonNullable<ReferralBar_currentUser>;
};

const Content = ({ currentUser, sport }: Props) => {
  const { referralAsReferee, refereeRewards } = currentUser;
  const { isClaimed } = useReferralRewards();
  const [openClaimDialog, toggleOpenClaimDialog] = useToggle(false);

  if (!referralAsReferee) {
    return null;
  }

  const { state } = referralAsReferee;

  const allRewardsClaimed =
    refereeRewards.length > 0 &&
    refereeRewards.every(reward => isClaimed(reward));

  if (allRewardsClaimed && state === ReferralState.COMPLETED) {
    return (
      <BodyM>
        <FormattedMessage
          id="ReferralBar.completed"
          defaultMessage="Congrats! You've completed the Referral Program."
        />
      </BodyM>
    );
  }
  if ([ReferralState.EXPIRED, ReferralState.BLOCKED].includesValue(state)) {
    return (
      <BodyM>
        <FormattedMessage
          id="ReferralBar.expired"
          defaultMessage="Too late! You didn't complete the Referral Program in time."
        />
      </BodyM>
    );
  }
  if (refereeRewards.length > 0 && (!allRewardsClaimed || openClaimDialog)) {
    return (
      <ReadyToClaimReferralBar
        openClaimDialog={openClaimDialog}
        toggleOpenClaimDialog={toggleOpenClaimDialog}
        refereeRewards={refereeRewards}
      />
    );
  }
  return <InProgressReferralBar referral={referralAsReferee} sport={sport} />;
};

export const ReferralBar = ({ currentUser, sport }: Props) => {
  const { referralAsReferee, refereeRewards } = currentUser;

  if (!referralAsReferee) {
    return null;
  }

  const { expirationDate, referrer, state } = referralAsReferee;

  return (
    <Vertical gap={2}>
      <SBHorizontal>
        <Badge
          badgeContent={
            refereeRewards.filter(
              r => r.claimableState !== ReferralRewardState.CLAIMED
            ).length
          }
        >
          <BodyL bold>
            <FormattedMessage
              id="InProgressReferralBar.title"
              defaultMessage="Your progression"
            />
          </BodyL>
        </Badge>
        {[ReferralState.IN_PROGRESS, ReferralState.EXPIRED].includesValue(
          state
        ) && (
          <span>
            <TimeLeft time={expirationDate} withExplicitTime />
          </span>
        )}
      </SBHorizontal>
      <Content currentUser={currentUser} sport={sport} />
      <BodyM color="var(--c-nd-600)">
        {isType(referrer, 'User') ? (
          <Horizontal>
            <FormattedMessage
              id="ReferralBar.referrer"
              defaultMessage="Referred by {referrer}"
              values={{
                referrer: (
                  <MyClubLink user={referrer}>
                    <Horizontal>
                      <ActiveUserAvatar user={referrer} size={3} />
                      <UserName user={referrer} />
                    </Horizontal>
                  </MyClubLink>
                ),
              }}
            />
          </Horizontal>
        ) : (
          <FormattedMessage
            id="ReferralBar.affiliate2"
            defaultMessage="Invited by a Sorare Affiliate"
          />
        )}
      </BodyM>
    </Vertical>
  );
};

ReferralBar.fragments = {
  currentUser: gql`
    fragment ReferralBar_currentUser on CurrentUser {
      slug
      refereeRewards {
        id
        claimableAt
        claimableState
        ...ReadyToClaimReferralBar_ReferralReward
        ...useReferralRewards_referralReward
      }
      referralAsReferee {
        id
        state
        expirationDate
        referrer {
          ... on User {
            slug
            ...ActiveUserAvatar_user
            ...UserName_publicUserInfoInterface
            ...MyClubLink_publicUserInfoInterface
          }
          ... on UserSource {
            id
          }
        }
        ...InProgressReferralBar_Referral
      }
    }
    ${ReadyToClaimReferralBar.fragments.referralReward}
    ${useReferralRewards.fragments.referralReward}
    ${MyClubLink.fragments.user}
    ${InProgressReferralBar.fragments.referral}
    ${ActiveUserAvatar.fragments.user}
    ${UserName.fragments.user}
  ` as TypedDocumentNode<ReferralBar_currentUser>,
};
